import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { EtatReseau } from '@stockflow/offline-core';
import { environment } from '../../../environments/environment';

/**
 * Reservation de stock vue du POS — ecart S1.
 *
 * CE QUE CA RESOUT. Entre l'ajout au panier et l'encaissement, rien
 * n'empechait une seconde caisse de vendre la meme unite. Le POS affichait une
 * disponibilite theorique.
 *
 * HORS LIGNE, ON N'EMPECHE RIEN — ET C'EST VOULU.
 *
 * Reserver exige le serveur. Or ce POS vend hors ligne : les ventes partent en
 * file et se transmettent au retour du reseau (ecart Y0). Refuser un ajout au
 * panier parce qu'Internet est tombe arreterait le commerce — un remede pire
 * que le mal.
 *
 * Hors ligne, on ajoute donc sans reserver, en acceptant le risque de survente
 * pendant la coupure. C'est deja la situation actuelle : on ne l'aggrave pas,
 * on cesse simplement de l'ameliorer le temps de la panne. La garantie revient
 * avec le reseau.
 *
 * Mettre la reservation dans la file des ventes a ete ecarte : une reservation
 * qui arrive apres coup ne protege plus rien, puisqu'au moment ou elle est
 * traitee la vente l'est aussi.
 */
@Injectable({ providedIn: 'root' })
export class ReservationsService {
  private readonly http = inject(HttpClient);
  private readonly reseau = inject(EtatReseau);
  private readonly url = `${environment.apiUrls.inventory}/reservations`;

  /**
   * Reserve une quantite d'un produit pour ce poste.
   *
   * @returns true si la reservation est prise OU si on est hors ligne ;
   *   false si le serveur a refuse faute de stock disponible.
   *
   * L'appelant BLOQUE sur cette promesse : l'article n'entre au panier
   * qu'une fois reserve. Un aller-retour de l'ordre de cent millisecondes est
   * le prix d'une garantie reelle, et c'est la seule facon de refuser
   * clairement plutot que de faire disparaitre une ligne sous les yeux du
   * vendeur.
   */
  async reserver(demande: {
    productId: string;
    warehouseId: number;
    variantId?: number | null;
    quantity: number;
    ownerId: string;
  }): Promise<{ pris: boolean; message?: string }> {
    if (!this.reseau.enLigne()) return { pris: true };

    try {
      await firstValueFrom(
        this.http.post(`${this.url}/produit`, {
          productId: demande.productId,
          warehouseId: demande.warehouseId,
          variantId: demande.variantId ?? null,
          quantity: demande.quantity,
          ownerKind: 'CART',
          ownerId: demande.ownerId,
        }),
      );
      return { pris: true };
    } catch (err: unknown) {
      const e = err as { status?: number; error?: { message?: string } };
      // 409 : le stock ne suffit pas. C'est un refus METIER, a montrer au
      // vendeur avec le chiffre disponible.
      if (e.status === 409) {
        return { pris: false, message: e.error?.message };
      }
      /**
       * Toute autre panne laisse passer.
       *
       * Un service de reservation indisponible ne doit pas bloquer une vente :
       * on retombe sur le comportement d'avant, celui qu'on avait avant ce
       * lot. Echouer fermeture serait transformer une amelioration en point
       * de panne unique.
       */
      return { pris: true };
    }
  }

  /**
   * Libere tout ce que tient ce poste.
   *
   * Appele a l'encaissement — la vente prend le relais — et a l'abandon du
   * panier. Idempotent : rejouer ne libere rien de plus.
   */
  async libererPanier(ownerId: string): Promise<void> {
    if (!this.reseau.enLigne()) return;
    try {
      await firstValueFrom(
        this.http.delete(
          `${this.url}/proprietaire?ownerKind=CART&ownerId=${encodeURIComponent(ownerId)}`,
        ),
      );
    } catch {
      /**
       * L'echec de liberation n'est pas bloquant.
       *
       * Les reservations d'un panier expirent d'elles-memes au bout de trente
       * minutes. Faire echouer un encaissement parce que la liberation n'a pas
       * abouti serait disproportionne.
       */
    }
  }

  /** Quantite reellement vendable d'une ligne de stock. */
  async disponible(stockId: string): Promise<number | null> {
    if (!this.reseau.enLigne()) return null;
    try {
      const r = await firstValueFrom(
        this.http.get<{ disponible: number }>(`${this.url}/disponible/${stockId}`),
      );
      return r.disponible;
    } catch {
      return null;
    }
  }
}
